/**
 * Order Review modal — VOX Store Phase 1 (regional pricing via
 * company-registered country).
 *
 * Flow on open:
 *   1. Fetch /api/subscriptions/tenant-profile.
 *   2. If a `registered_country` is already stored on the tenant, use
 *      it and DO NOT ask the buyer again (frictionless).
 *   3. Otherwise show one prompt: "Where is your company registered?"
 *      backed by a searchable country combobox.
 *
 * Region (`sea` vs `row`) is derived server-side from that country —
 * the buyer never sees SEA/ROW. India + SEA countries automatically
 * receive SEA pricing; every other country receives ROW pricing.
 *
 * IP / physical location is NEVER used to determine pricing or block
 * signup.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import CountryCombobox from '@/components/subscription/CountryCombobox';
import { COUNTRIES } from '@/lib/satisfinderCountries';

const PRODUCT_LABEL = {
  nps: 'NPS®',
  csat: 'CSAT',
  voc: 'Voice of Customer',
  voe: 'Voice of Employee',
};

// Countries in the India + SEA pricing band (mirrors the backend
// resolver in routes/subscriptions.py). Used purely to render a
// preview total client-side. The final region + Stripe Price ID is
// always chosen server-side from `plans.regional_prices`.
const SEA_ISO = new Set(['IN','PH','SG','MY','TH','ID','VN','KH','LA','MM','BN','TL']);
const SEA_NAMES = new Set([
  'india','philippines','singapore','malaysia','thailand','indonesia',
  'vietnam','cambodia','laos','myanmar','brunei','timor-leste','east timor',
]);

function regionForCountry(countryOrIso) {
  if (!countryOrIso) return 'row';
  const raw = String(countryOrIso).trim();
  if (SEA_ISO.has(raw.toUpperCase())) return 'sea';
  if (SEA_NAMES.has(raw.toLowerCase())) return 'sea';
  const c = COUNTRIES.find(x => x.name.toLowerCase() === raw.toLowerCase());
  if (c && SEA_ISO.has(c.iso)) return 'sea';
  return 'row';
}

export default function OrderReviewModal({ open, onOpenChange, product, plan, initialCycle }) {
  const [cycle, setCycle] = useState(initialCycle || 'monthly');
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState('');
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setCycle(initialCycle || 'monthly');
    setCount(1);
    setCountry('');
    setProfileLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/subscriptions/tenant-profile`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          // Preselect the stored country if one is on file. Otherwise,
          // fall back to the edge's IP geolocation hint (ISO-2 code) —
          // the customer can still change it. Regional pricing is
          // ALWAYS decided by the customer-confirmed value at submit,
          // never by IP.
          if (data.registered_country) {
            setCountry(data.registered_country);
          } else if (data.ip_country_hint) {
            setCountry(data.ip_country_hint);
          } else {
            // Server had no CF-IPCountry hint (Cloudflare zone
            // configuration or non-CF traffic). Try a lightweight
            // public IP-geolocation as a last-ditch hint. Runs from
            // the browser so it doesn't block checkout, no API key,
            // no data stored server-side. If the call fails or the
            // service returns nothing, the picker simply stays empty.
            try {
              const ipRes = await fetch('https://ipwho.is/', { cache: 'no-store' });
              if (ipRes.ok) {
                const ipData = await ipRes.json();
                const iso = (ipData?.country_code || '').toUpperCase();
                if (iso && iso.length === 2) setCountry(iso);
              }
            } catch { /* ignore geolocation errors */ }
          }
        }
      } catch { /* ignore — buyer will just have to pick */ }
      finally { setProfileLoading(false); }
    })();
  }, [open, initialCycle]);

  const region = regionForCountry(country);

  const unitPrice = useMemo(() => {
    if (!plan) return 0;
    const regional = (plan.regional_prices || {})[region] || {};
    if (cycle === 'annual') return regional.annual_price ?? plan.annual_price ?? 0;
    return regional.monthly_price ?? plan.monthly_price ?? 0;
  }, [plan, region, cycle]);

  const total = unitPrice * count;
  const canPay = !!country && count >= 1;

  const submit = async () => {
    if (!product || !canPay) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product,
          billing_cycle: cycle,
          location_count: count,
          registered_country: country,
          origin_url: window.location.origin,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      }
      const err = await res.json().catch(() => ({}));
      const detail = typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Could not start checkout');
      toast.error(detail);
    } catch (e) {
      toast.error('Could not start checkout');
    } finally {
      setBusy(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="order-review-modal">
        <DialogHeader>
          <DialogTitle className="font-['Montserrat']">Review your order</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="border border-[#E2E8F0] rounded-lg p-4">
            <p className="text-xs uppercase tracking-wider text-[#64748B]">Product</p>
            <p className="text-lg font-semibold text-[#0F172A]" data-testid="order-product">{PRODUCT_LABEL[product] || product}</p>
          </div>

          {profileLoading ? (
            <div className="flex items-center gap-2 text-sm text-[#64748B]" data-testid="order-profile-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your company profile…
            </div>
          ) : (
            <div data-testid="order-country-picker">
              <Label className="text-xs uppercase tracking-wider text-[#64748B]">
                Registered country
              </Label>
              <div className="mt-1">
                <CountryCombobox value={country} onChange={setCountry} testId="order-country" />
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">
                Select the country where your business is registered. This determines your regional pricing and is saved to your company profile.
              </p>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#64748B]">Billing cycle</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {['monthly', 'annual'].map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCycle(c)}
                  data-testid={`order-cycle-${c}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    cycle === c ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]' : 'bg-white text-[#0F172A] border-[#E2E8F0]'
                  }`}
                >
                  {c === 'monthly' ? 'Monthly' : 'Annual — pay 10 months, get 12'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="loc-count" className="text-xs uppercase tracking-wider text-[#64748B]">
              Number of locations
            </Label>
            <div className="mt-1 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#64748B]" />
              <Input
                id="loc-count"
                type="number"
                min={1}
                max={9999}
                value={count}
                onChange={e => setCount(Math.max(1, parseInt(e.target.value || '1', 10)))}
                data-testid="order-location-count"
                className="w-32"
              />
              <span className="text-xs text-[#64748B]">
                × {country ? `$${unitPrice}/${cycle === 'annual' ? 'yr' : 'mo'}` : '—'}
              </span>
            </div>
          </div>

          <div className="border-t border-[#E2E8F0] pt-3 flex items-baseline justify-between">
            <span className="text-sm text-[#64748B]">Total {cycle === 'annual' ? 'per year' : 'per month'}</span>
            <span className="text-2xl font-bold text-[#0F172A]" data-testid="order-total">
              {country ? <>${total} <span className="text-xs text-[#94A3B8]">USD</span></> : <span className="text-sm text-[#94A3B8]">Pick your country to see pricing</span>}
            </span>
          </div>

          <p className="text-[11px] text-[#94A3B8]">
            You'll be redirected to Stripe to complete payment.
            {cycle === 'annual'
              ? ' Annual plans commit for 12 months — you can turn off auto-renewal in your account any time.'
              : ' Monthly plans renew automatically — you can turn off auto-renewal in your account any time.'}
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="order-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !canPay}
            className="bg-[#1A3E6F] hover:bg-[#122c50] text-white"
            data-testid="order-confirm-pay"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</> : `Continue to payment`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
